import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Heading,
  Radio,
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  Textarea,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Spinner,
  Center
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { ContentItem } from '../../types';

export interface QuizQuestion {
  id: string;
  question: string;
  type: 'single' | 'multiple' | 'text';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
}

export interface QuizData {
  title: string;
  description?: string;
  questions: QuizQuestion[];
  passingScore?: number; // percentage
  allowRetake?: boolean;
  showCorrectAnswers?: boolean;
}

export interface QuizContentProps {
  content: ContentItem & { data?: QuizData };
  // onComplete?: (contentId: string) => void;
  onSubmit?: (contentId: string, answers: Record<string, any>) => void;
  score?: number;
  passed?: boolean;
  showResults?: boolean;
  isSubmitting?: boolean;
  onResultsReady?: () => void;
  t?: (key: string, options?: any) => string | any;
}

export function QuizContent({ content, onSubmit, score, passed, showResults: showResultsProp, isSubmitting, t }: QuizContentProps) {
  // Fallback translation function if not provided
  const translate = t || ((key: string, options?: any) => {
    const translations: Record<string, string> = {
      'quiz.submitting': 'Submitting Quiz...',
      'quiz.please_wait': 'Please wait while we calculate your score.',
      'quiz.results': 'Quiz Results',
      'quiz.congratulations': 'Congratulations!',
      'quiz.try_again': 'Try Again',
      'quiz.your_score': `You scored ${options?.score || 0}% on this quiz.`,
      'quiz.passed': ' You have passed!',
      'quiz.need_score': ` You need ${options?.score || 70}% to pass.`,
      'quiz.score_info': `Score: ${options?.score || 0}% (${options?.correct || 0}/${options?.total || 0})`,
      'quiz.review_answers': 'Review Answers',
      'quiz.your_answer': `Your answer: ${options?.answer || 'No answer'}`,
      'quiz.no_answer': 'No answer',
      'quiz.correct_answer': `Correct answer: ${options?.answer || ''}`,
      'quiz.retake_quiz': 'Retake Quiz',
      'quiz.of': 'of',
      'quiz.previous': 'Previous',
      'quiz.next': 'Next',
      'quiz.submit_quiz': 'Submit Quiz',
      'quiz.text_placeholder': 'Enter your answer here...',
      'quiz.sample_description': 'Sample quiz content',
      'quiz.sample_question_1': 'What is the capital of France?',
      'quiz.sample_option_1_1': 'London',
      'quiz.sample_option_1_2': 'Berlin',
      'quiz.sample_option_1_3': 'Paris',
      'quiz.sample_option_1_4': 'Madrid',
      'quiz.sample_explanation_1': 'Paris is the capital and largest city of France.',
      'quiz.sample_question_2': 'Which of the following are programming languages?',
      'quiz.sample_option_2_1': 'JavaScript',
      'quiz.sample_option_2_2': 'HTML',
      'quiz.sample_option_2_3': 'Python',
      'quiz.sample_option_2_4': 'CSS',
      'quiz.sample_explanation_2': 'JavaScript and Python are programming languages, while HTML and CSS are markup and styling languages.',
      'quiz.sample_question_3': 'Explain the concept of recursion in programming.',
      'quiz.sample_explanation_3': 'Recursion is a programming technique where a function calls itself to solve a problem.'
    };
    return translations[key] || key;
  });

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const boxBgColor = useColorModeValue('gray.50', 'gray.700');
  // Update local showResults when prop changes
  useEffect(() => {
    if (showResultsProp && score !== undefined) {
      setShowResults(true);
    } else if (!showResultsProp) {
      setShowResults(false);
    }
  }, [showResultsProp, score]);

  const quizData: QuizData = content.data || {
    title: content.title,
    description: translate('quiz.sample_description'),
    questions: [
      {
        id: '1',
        question: translate('quiz.sample_question_1'),
        type: 'single',
        options: [translate('quiz.sample_option_1_1'), translate('quiz.sample_option_1_2'), translate('quiz.sample_option_1_3'), translate('quiz.sample_option_1_4')],
        correctAnswer: translate('quiz.sample_option_1_3'),
        explanation: translate('quiz.sample_explanation_1')
      },
      {
        id: '2',
        question: translate('quiz.sample_question_2'),
        type: 'multiple',
        options: [translate('quiz.sample_option_2_1'), translate('quiz.sample_option_2_2'), translate('quiz.sample_option_2_3'), translate('quiz.sample_option_2_4')],
        correctAnswer: [translate('quiz.sample_option_2_1'), translate('quiz.sample_option_2_3')],
        explanation: translate('quiz.sample_explanation_2')
      },
      {
        id: '3',
        question: translate('quiz.sample_question_3'),
        type: 'text',
        explanation: translate('quiz.sample_explanation_3')
      }
    ],
    passingScore: 70,
    allowRetake: true,
    showCorrectAnswers: true
  };

  const currentQuestion = quizData.questions[currentQuestionIndex];
  const totalQuestions = quizData.questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = () => {
    setIsSubmitted(true);

    onSubmit?.(content.id, {
      answers,
      completedAt: new Date().toISOString()
    });
  };

  const handleRetake = () => {
    setAnswers({});
    setIsSubmitted(false);
    setShowResults(false);
    setCurrentQuestionIndex(0);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const renderQuestion = (question: QuizQuestion) => {
    const userAnswer = answers[question.id];

    switch (question.type) {
      case 'single':
        return (
          <RadioGroup
            value={userAnswer || ''}
            onChange={(value) => handleAnswerChange(question.id, value)}
          >
            <VStack align="stretch" spacing={3}>
              {question.options?.map((option, index) => (
                <Radio key={index} value={option} isDisabled={isSubmitted}>
                  {option}
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        );

      case 'multiple':
        return (
          <CheckboxGroup
            value={userAnswer || []}
            onChange={(value) => handleAnswerChange(question.id, value)}
          >
            <VStack align="stretch" spacing={3}>
              {question.options?.map((option, index) => (
                <Checkbox key={index} value={option} isDisabled={isSubmitted}>
                  {option}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        );

      case 'text':
        return (
          <Textarea
            value={userAnswer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={translate('quiz.text_placeholder')}
            rows={4}
            isDisabled={isSubmitted}
          />
        );

      default:
        return null;
    }
  };

  if (isSubmitting) {
    return (
      <Box
        w="100%"
        h="100%"
        p={6}
      >
        <VStack spacing={6} align="stretch" justify="center" h="100%">
          <Heading size="lg" color={textColor} textAlign="center">
            {translate('quiz.submitting')}
          </Heading>
          <Center>
            <Spinner size="xl" />
          </Center>
          <Text color="gray.600" textAlign="center">
            {translate('quiz.please_wait')}
          </Text>
        </VStack>
      </Box>
    );
  }

  if (showResults || content.completed) {
    const finalScore = score;
    const finalPassed = passed !== undefined ? passed : finalScore && finalScore >= (quizData.passingScore || 70);

    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="auto"
        p={6}
      >
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color={textColor}>
            {translate('quiz.results')}
          </Heading>

          <Alert status={finalPassed ? 'success' : 'error'}>
            <AlertIcon />
            <Box>
              <AlertTitle>
                {finalPassed ? translate('quiz.congratulations') : translate('quiz.try_again')}
              </AlertTitle>
              <AlertDescription>
                {translate('quiz.your_score', { score: parseFloat(finalScore?.toString() || '0').toFixed(1) })}
                {finalPassed
                  ? translate('quiz.passed')
                  : translate('quiz.need_score', { score: quizData.passingScore || 70 })
                }
              </AlertDescription>
            </Box>
          </Alert>

          <Box>
            <Text fontSize="lg" fontWeight="medium" mb={4}>
              {translate('quiz.score_info', {
                score: parseFloat(finalScore?.toString() || '0').toFixed(1),
                correct: Math.round(parseFloat(finalScore?.toString() || '0') * totalQuestions / 100),
                total: totalQuestions
              })}
            </Text>
            <Progress value={parseFloat(finalScore?.toString() || '0')} colorScheme={finalPassed ? 'green' : 'red'} />
          </Box>

          {quizData.showCorrectAnswers && (
            <Box>
              <Heading size="md" mb={4}>{translate('quiz.review_answers')}</Heading>
              <VStack spacing={4} align="stretch">
                {quizData.questions.map((question, index) => (
                  <Box key={question.id} p={4} borderRadius="md" bg={boxBgColor}>
                    <Text fontWeight="medium" mb={2}>
                      {index + 1}. {question.question}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      {translate('quiz.your_answer', {
                        answer: Array.isArray(answers[question.id])
                          ? answers[question.id].join(', ')
                          : answers[question.id] || translate('quiz.no_answer')
                      })
                      }
                    </Text>
                    {question.correctAnswer && (
                      <Text fontSize="sm" color="green.600" mb={2}>
                        {translate('quiz.correct_answer', {
                          answer: Array.isArray(question.correctAnswer)
                            ? question.correctAnswer.join(', ')
                            : question.correctAnswer
                        })
                        }
                      </Text>
                    )}
                    {question.explanation && (
                      <Text fontSize="sm" color="gray.600">
                        {question.explanation}
                      </Text>
                    )}
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          <HStack spacing={4}>
            {quizData.allowRetake && !finalPassed && (
              <Button colorScheme="blue" onClick={handleRetake}>
                {translate('quiz.retake_quiz')}
              </Button>
            )}
          </HStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      h="100%"
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="auto"
      p={6}
    >
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack justify="space-between" align="center" mb={4}>
            <Heading size="lg" color={textColor}>
              {quizData.title}
            </Heading>
            <Badge colorScheme="blue">
              {currentQuestionIndex + 1} {translate('quiz.of')} {totalQuestions}
            </Badge>
          </HStack>

          {quizData.description && (
            <Text color="gray.600" mb={4}>
              {quizData.description}
            </Text>
          )}

          <Progress value={progress} colorScheme="blue" mb={4} />
        </Box>

        <Box flex={1}>
          <VStack spacing={4} align="stretch">
            <Heading size="md" color={textColor}>
              {currentQuestion.question}
            </Heading>

            {renderQuestion(currentQuestion)}
          </VStack>
        </Box>

        <HStack justify="space-between">
          <Button
            variant="outline"
            onClick={prevQuestion}
            isDisabled={currentQuestionIndex === 0}
          >
            {translate('quiz.previous')}
          </Button>

          <HStack>
            {currentQuestionIndex < totalQuestions - 1 ? (
              <Button
                colorScheme="blue"
                onClick={nextQuestion}
                isDisabled={!answers[currentQuestion.id]}
              >
                {translate('quiz.next')}
              </Button>
            ) : (
              <Button
                colorScheme="green"
                onClick={handleSubmit}
                isDisabled={Object.keys(answers).length < totalQuestions}
              >
                {translate('quiz.submit_quiz')}
              </Button>
            )}
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
} 